import type { ReleaseNotePlatform } from './api';

/**
 * 업데이트 내역의 **표시 어휘** — surface·분류의 라벨·색과 날짜 변환.
 *
 * ## 왜 페이지 밖으로 나왔나 — 2026-10-21 (결정 채택 ⑥)
 * 노출면이 둘로 갈리면서 **두 화면이 같은 표를 읽는다**:
 *  - `pages/ReleaseNotes.tsx` — **관리** 뷰(숨김·복구·노출 대상, `system-settings` 권한 뒤)
 *  - `pages/ReleaseNoteFeed.tsx` — **소비** 뷰(읽기만, 수퍼관리자 전원)
 *
 * ⚠️ **사본을 두지 않는다.** 라벨·색 표가 두 벌이면 한쪽만 고치는 날 두 화면의 같은 surface가
 * **다른 이름·다른 색**으로 보이는데 **그 갈라짐에는 red가 없다**(둘 다 유효한 문자열이다).
 *
 * ## 🔴 그 사본 낡음을 «무엇도» 세지 않는다 — 이 파일이 하나인 것이 유일한 방어다
 * `scripts/release-notes/frontend-surface-copies.test.mjs`를 그 근거로 **인용하지 말 것.** 그 검사의
 * 분모는 **surface 유니온 선언**과 `LEGACY_SURFACES` 둘뿐이고 **라벨·색 표는 거기 없다**(그 파일의
 * 「이 검사가 구조적으로 못 보는 범주」 절이 그 경계를 적는다). 즉 **그 검사의 초록은 「라벨 사본이
 * 없다」를 조금도 뜻하지 않는다** — 누가 둘째 표를 만들어도 그쪽은 912/912로 통과한다.
 * ⇒ 그래서 「공용으로 둔다」가 규칙이 아니라 **구조**여야 한다. 표를 다시 페이지로 내리거나 둘째
 * 벌을 만들려면 **먼저 그 낡음을 세는 검사를 만들어라**(없으면 그 상태가 영영 조용하다).
 *
 * ⚠️ tsc도 그 축을 보지 않는다 — 두 표가 각각 `Record<Platform, string>`을 충족하면 **값이 서로
 * 달라도** 컴파일된다. CI는 admin을 Vite(esbuild)로 빌드해 타입조차 보지 않는다.
 *
 * ## ⚠️ 여기 **옮기지 않은** 것 — 관리 뷰 고유다
 * `LEGACY_SURFACES`·`PLATFORM_FILTER_OPTIONS`(필터 드롭다운 축) · `STATUS_LABELS`·`STATUS_COLORS` ·
 * `EMPTY_PLATFORMS_*`는 `pages/ReleaseNotes.tsx`에 **남겼다.** 그 JSDoc들의 근거가 **그 화면
 * 고유**이기 때문이다 — 예컨대 빈 집합 칩이 회색을 피하는 이유가 「이 화면에서 회색은 이미
 * `ALL` 칩·모르는 값 폴백·**`ARCHIVED` 상태 칩** 셋을 뜻한다」인데, 소비 뷰에는 **상태 칩이
 * 없다.** 공용으로 올리면 그 근거가 한쪽에서 거짓이 된다. ⇒ 「같이 쓰니 같이 올리자」로 옮기지
 * 말 것 — 올릴 것은 **두 화면이 같은 이유로 읽는 것**뿐이다.
 *
 * ⚠️ 여기에 **surface 유니온을 선언하지 말 것** — 그 선언은 `lib/api.ts` 한 자리가 소유하고(그 파일
 * 주석) 여기 적으면 사본이 둘이 되어 그 경로 래칫이 red가 된다. 이 파일은 그 타입을 **import만** 한다.
 *
 * ⚠️ **그 금지를 적을 때 선언 표기를 그대로 쓰지도 말 것** — 2026-10-21에 이 자리가 그 문장을
 * 리터럴로 적어 **주석이 사본으로 집계됐다**(`frontend-surface-copies.test.mjs`의 needle이
 * `type <그 이름> =`이고 코드·주석을 가리지 않는다). 즉 그 래칫은 제 일을 했고, 금지를 적는
 * 쪽이 needle을 피해야 한다(같은 함정의 정본은 CLAUDE.md의 「census를 설명하는 주석에 needle에
 * 걸리는 표기를 쓰지 말 것」).
 */

/** surface 값 — 선언은 `lib/api.ts`의 {@link ReleaseNotePlatform} **한 자리**가 소유한다. */
export type Platform = ReleaseNotePlatform;

/** 분류 — 서버 `ReleaseNoteCategory` enum 네 값. 두 화면이 같은 표로 그린다. */
export type Category = 'FEATURE' | 'IMPROVEMENT' | 'BUGFIX' | 'SECURITY';

/**
 * surface 값의 화면 문구 — 일곱 값 전부에 대해 exhaustive하다.
 *
 * **세 자리가 이것을 읽는다**: 관리 뷰 행의 칩(`note.platforms`의 **원소마다** —
 * `platformLabel(p)`) · 관리 뷰의 **필터 드롭다운**(`pages/ReleaseNotes.tsx`의
 * `PLATFORM_FILTER_OPTIONS`가 이 표에서 파생된다. 옵션을 손열거하지 않는다) · **소비 뷰**의 칩.
 * 그래서 값을 더하면 드롭다운 옵션도 함께 생긴다.
 *
 * ⚠️ **표와 옵션 목록은 같지 않다** — 옵션은 이 표에서 **유산 값을 뺀 것**이다
 * (`pages/ReleaseNotes.tsx`의 `LEGACY_SURFACES`). 표를 그에 맞춰 좁히지 말 것: 유산 값을 든
 * **옛 행**이 목록에 뜨면 칩이 서버가 준 값을 그대로 그리므로, 라벨이 없으면 아래
 * {@link platformLabel} 폴백으로 **raw enum이 화면에 뜬다**.
 *
 * ⚠️ **빠진 값이 있으면 라벨이 `undefined`로 그려져 빈 칩이 된다** — 2026-10-15 재분류 전까지
 * `APP`·`INTERNAL`이 빠져 있었고, 그 상태로 재분류가 반영되면 `INTERNAL` 145 + `APP` 3 = **148행이
 * 빈 칩**이 될 자리였다. 관리 뷰는 `INTERNAL`이 **보여야 하는** 화면이다(소비 뷰·공개 피드만
 * 배제한다 — 소비 뷰의 배제는 **서버가** 한다: `release-notes.service.ts`의 `findConsumerFeed`).
 *
 * ## ⚠️ 이 안전을 무엇이 지키나 — 한 층만 소유자가 있다
 * - **「Record가 일곱 값을 갖는다」**: `Record<Platform, string>`이 **tsc로 exhaustive를 강제**한다.
 *   값을 빠뜨리면 컴파일이 안 된다. ⚠️ 단 CI는 admin을 Vite(esbuild)로 빌드해 **타입을 보지
 *   않으므로** 그 게이트는 **로컬 `pnpm exec tsc --noEmit`뿐**이다.
 * - **「그 행이 화면에서 라벨을 갖는다」**: `pages/__tests__/releaseNotesPlatformChips.test.tsx`가
 *   관리 목록을 렌더해 칩을 잰다(2026-10-16 신설 — 그 전엔 이 페이지를 렌더하는 spec이 admin에
 *   **0건**이었고 그 공백이 「칩이 대표값만 그리던 결함」을 수개월 가렸다).
 *   ⚠️ 그래도 **tsc 초록을 「화면도 쟀다」로 읽지 말 것** — 두 층은 여전히 다르다. 갈리는 자리는
 *   하나다: 서버 enum이 **여덟 번째 값**을 얻고 `lib/api.ts`의 유니온 사본이 낡으면 **타입이
 *   거짓이 되어 tsc가 침묵**한다(2026-10-15 결함이 정확히 그 기제였다). 그때 화면이 무엇을
 *   보여주나는 아래 {@link platformLabel} 폴백이 소유하고, 사본이 낡는 것 **자체**는
 *   `scripts/release-notes/frontend-surface-copies.test.mjs`가 막는다.
 */
export const PLATFORM_LABELS: Record<Platform, string> = {
  ALL: '전체',
  WEB: '웹',
  MOBILE: '모바일',
  APP: '앱',
  ADMIN: '관리자',
  SERVER: '서버',
  INTERNAL: '내부',
};

// APP은 MOBILE과 **같은 green**이다 — 같은 surface의 선행/후계 값이라 색을 가르면 둘이 다른 것으로
// 읽힌다(과도기에 두 값이 한 목록에 섞인다). 구분은 라벨 텍스트가 한다.
// INTERNAL은 중립 회색 — 「공개되지 않는다」를 색으로도 읽히게 둔다(ALL과는 라벨이 가른다).
export const PLATFORM_COLORS: Record<Platform, string> = {
  ALL: 'bg-gray-100 text-gray-800',
  WEB: 'bg-blue-100 text-blue-800',
  MOBILE: 'bg-green-100 text-green-800',
  APP: 'bg-green-100 text-green-800',
  ADMIN: 'bg-purple-100 text-purple-800',
  SERVER: 'bg-orange-100 text-orange-800',
  INTERNAL: 'bg-slate-200 text-slate-700',
};

/**
 * 위 두 표에 **없는** surface 값이 왔을 때의 표시 — **raw 값을 그대로 보여준다**(사용자 결정 2026-10-15).
 *
 * ## 왜 일반 문구로 가리지 않나 — web과 **일부러 다르다**
 * 여기는 운영자 화면이고, 이 자리에 모르는 값이 뜨는 것은 **「{@link Platform} 사본(`lib/api.ts`)이
 * 낡았다」는 신호**다. 「기타」처럼 접으면 그 신호가 **사라져** 빈 칩도 아닌 그럴듯한 화면이 된다.
 * 고객 화면(`apps/web/src/pages/release-notes/index.tsx`)은 반대로 일반 문구로 떨어진다 — 거기서
 * 내부 어휘는 뜻이 통하지 않고 고객이 할 수 있는 일도 없다
 * (`memory/feedback-display-fallback-leaks-raw-enum`). ⇒ **두 앱에 같은 폴백을 넣지 말 것.**
 *
 * ⚠️ **admin 두 화면은 같은 폴백을 쓴다** — 그것이 이 함수가 공용인 이유다. 소비 뷰가 운영자
 * 화면이라는 점은 관리 뷰와 다르지 않다(둘 다 수퍼관리자가 본다).
 *
 * ## 이 폴백이 덮는 창 — **사본 래칫의 대체가 아니다**
 * 사본이 낡는 것 자체는 `scripts/release-notes/frontend-surface-copies.test.mjs`가 **양방향 래칫**으로
 * 막는다. 그 검사가 초록이어도 남는 창이 둘이다: 배포가 두 job으로 갈려 서버 enum이 먼저 넓어질 수
 * 있고(`deploy-admin`은 Cloudflare, 마이그레이션은 `deploy-ec2`), 더 긴 창은 **캐시된 옛 번들**이다.
 * ⇒ 둘은 다른 것을 막으므로 한쪽을 다른 쪽의 근거로 쓰지 말 것.
 *
 * ⚠️ 이것은 위 두 표를 **좁히는 것이 아니다** — 일곱 값은 그대로 유지한다(위 Record JSDoc).
 * ⚠️ **tsc는 이 폴백을 재지 않는다** — `Record<Platform, string>` 조회는 늘 `string`으로 타입되므로
 * 이 `??`가 **불필요해 보인다.** 그것이 바로 이 폴백이 필요한 이유다 — 낡은 창에서는 **타입이
 * 거짓**이고 런타임 값이 그 유니온 밖이다. 「죽은 코드」로 읽고 지우지 말 것.
 * **이 축의 소유자는 2026-10-16에 생겼다**: `pages/__tests__/releaseNotesPlatformChips.test.tsx`의
 * **「[되돌림] 모르는 surface는 raw 값 그대로 보인다 — 「기타」로 접지 않는다」**. 종전에는 탐지기가
 * 없었고, 그때는 web 짝 spec
 * (`apps/web/src/pages/release-notes/__tests__/index.defaultScope.test.tsx`)의 「모르는 surface」
 * 케이스가 **web의 문구 폴백**만 쟀다 — 두 앱이 일부러 다른 폴백을 쓰므로 그 케이스는 이쪽 동작을
 * 증명하지 않는다. ⇒ **두 케이스를 서로의 근거로 인용하지 말 것**(반대 방향을 단언한다).
 *
 * ⚠️ **관리 뷰의 필터 드롭다운에는 쓰지 않는다** — 그 옵션은 `PLATFORM_FILTER_OPTIONS`(라벨 표의
 * 부분집합)에서 파생되므로 모르는 값이 **도달할 수 없다**. 쓰면 영영 발동하지 않는 분기가 되고
 * 그것은 거짓 안심이다.
 */
export const platformLabel = (platform: Platform): string => PLATFORM_LABELS[platform] ?? platform;
export const platformColor = (platform: Platform): string =>
  PLATFORM_COLORS[platform] ?? 'bg-gray-100 text-gray-800';

export const CATEGORY_LABELS: Record<Category, string> = {
  FEATURE: '새 기능',
  IMPROVEMENT: '개선',
  BUGFIX: '버그 수정',
  SECURITY: '보안',
};

export const CATEGORY_COLORS: Record<Category, string> = {
  FEATURE: 'bg-emerald-100 text-emerald-800',
  IMPROVEMENT: 'bg-sky-100 text-sky-800',
  BUGFIX: 'bg-rose-100 text-rose-800',
  SECURITY: 'bg-amber-100 text-amber-800',
};

// UTC를 KST 날짜 문자열로 변환
export const toKSTDateString = (isoString: string): string => {
  const date = new Date(isoString);
  const kstOffset = 9 * 60 * 60 * 1000; // 9시간
  const kstDate = new Date(date.getTime() + kstOffset);
  return kstDate.toISOString().split('T')[0];
};
